import { useState, useEffect, useCallback } from 'react'
import type { RevenueSeasonalityRow } from '../utils/revenueSeasonalityUtils'

const PATHS = [
  '/json-data/revenue_seasonality.json',
  '/data_models/revenue_seasonality.json',
] as const

interface Result {
  records: RevenueSeasonalityRow[]
  loading: boolean
  error: string | null
  refetch: () => void
}

export function useRevenueSeasonality(): Result {
  const [records, setRecords] = useState<RevenueSeasonalityRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refetch = useCallback(() => setTick(t => t + 1), [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    ;(async () => {
      let lastErr: Error | null = null
      for (const path of PATHS) {
        try {
          const r = await fetch(path)
          if (!r.ok) {
            lastErr = new Error(`${path}: ${r.status}`)
            continue
          }
          const json = await r.json()
          if (!Array.isArray(json)) {
            lastErr = new Error('Revenue seasonality data must be a JSON array')
            continue
          }
          if (!cancelled) {
            setRecords(json as RevenueSeasonalityRow[])
            setLoading(false)
          }
          return
        } catch (e) {
          lastErr = e instanceof Error ? e : new Error(String(e))
        }
      }
      if (!cancelled) {
        setError(
          lastErr?.message || 'Failed to load revenue_seasonality.json'
        )
        setRecords([])
        setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [tick])

  return { records, loading, error, refetch }
}
